// On-screen PIN pad for the web. The PIN screens type into a hidden <input>, and a mobile browser opens its
// keyboard only for a focus made inside a tap — never for autoFocus or the delayed focus() the screens use —
// so on a phone there was no way to type the PIN. The pad is always there; the hidden input stays for a
// desktop keyboard, with `PIN_INPUT_PROPS` keeping the phone's own keyboard from popping up over the pad.
import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import { COLORS } from '../src/theme/themeTokens';
import { COLORS_WRONG } from '../src/theme/colorsWrong';

export const PIN_INPUT_PROPS = Platform.OS === 'web' ? ({ inputMode: 'none' } as const) : {};

type Props = {
  value: string;
  length: number;
  onChange: (next: string) => void;
  disabled?: boolean;
  compact?: boolean;
  style?: StyleProp<ViewStyle>;
};

const ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', 'del'],
];

export default function PinKeypad({ value, length, onChange, disabled, compact, style }: Props) {
  if (Platform.OS !== 'web') return null;

  const press = (key: string) => {
    if (disabled) return;
    if (key === 'del') onChange(value.slice(0, -1));
    else if (value.length < length) onChange(value + key);
  };

  return (
    <View style={[styles.pad, compact && styles.padCompact, style]}>
      {ROWS.map(row => (
        <View key={row.join()} style={styles.row}>
          {row.map((key, i) =>
            key ? (
              <TouchableOpacity
                key={key}
                style={[styles.key, compact && styles.keyCompact, key === 'del' && styles.keyGhost]}
                onPress={() => press(key)}
                disabled={disabled}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel={key === 'del' ? 'Delete' : key}
              >
                {/* U+232B with a text-presentation selector, as in NumericKeypad: drawn in the text colour, not as an emoji. */}
                <Text style={key === 'del' ? styles.delText : styles.keyText}>{key === 'del' ? '⌫︎' : key}</Text>
              </TouchableOpacity>
            ) : (
              <View key={`blank${i}`} style={[styles.key, compact && styles.keyCompact, styles.keyGhost]} />
            ),
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  pad: {
    width: '100%',
    maxWidth: 320,
    alignSelf: 'center',
    gap: 12,
    paddingHorizontal: 24,
  },
  padCompact: {
    paddingHorizontal: 0,
    gap: 8,
    marginTop: 8,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  key: {
    flex: 1,
    height: 56,
    borderRadius: 12,
    backgroundColor: COLORS.pureLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  keyCompact: {
    height: 48,
    backgroundColor: COLORS.white,
  },
  keyGhost: {
    backgroundColor: 'transparent',
  },
  keyText: {
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: '400',
    color: COLORS_WRONG.keypadText,
  },
  delText: {
    fontSize: 26,
    color: COLORS_WRONG.keypadText,
  },
});
